import { Button, Form, Modal } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { notify } from "../actions/CommonActions";
import { updateSingleUploadedFile } from "../../services";

const POLICY_SECTIONS = [
  {
    title: "Policy",
    fields: [
      { name: "policyNumber", label: "Policy number", type: "text" },
      { name: "insured", label: "Insured (employer name)", type: "text" },
      { name: "noOfEmployees", label: "Number of employees", type: "number" },
      { name: "commencementDate", label: "Commencement date", type: "date" },
      { name: "expiryDate", label: "Expiry date", type: "date" },
      { name: "policyDescription", label: "Policy description", type: "text" },
      { name: "typeId", label: "Type ID", type: "text" },
    ],
  },
  {
    title: "Employer",
    fields: [
      { name: "officeAddress", label: "Address", type: "text" },
      { name: "employerCity", label: "Employer city", type: "text" },
      { name: "employerState", label: "Employer state", type: "text" },
      { name: "postalCode", label: "Postal code", type: "text" },
      { name: "employerType", label: "Employer type", type: "text" },
      { name: "employerRcNumber", label: "Employer RC number", type: "text" },
      { name: "employerOccupation", label: "Employer occupation", type: "text" },
      { name: "employerEmail", label: "Employer email", type: "email" },
      { name: "employerNature", label: "Employer nature", type: "text" },
      { name: "mobileNumber", label: "Mobile number", type: "text" },
    ],
  },
  {
    title: "Product and premium",
    fields: [
      { name: "productName", label: "Product name", type: "text" },
      { name: "productType", label: "Product type", type: "number" },
      { name: "insuranceBenefit", label: "Insurance benefit", type: "text" },
      { name: "sumAssured", label: "Sum assured", type: "number" },
      { name: "premium", label: "Premium", type: "number" },
      { name: "commissionFee", label: "Commission fee", type: "number" },
      { name: "extraFee", label: "Extra fee", type: "number" },
    ],
  },
];

const NUMBER_FIELDS = POLICY_SECTIONS.flatMap((section) =>
  section.fields.filter((field) => field.type === "number").map((field) => field.name)
);

const DATE_FIELDS = POLICY_SECTIONS.flatMap((section) =>
  section.fields.filter((field) => field.type === "date").map((field) => field.name)
);

const emptyPolicy = () =>
  POLICY_SECTIONS.flatMap((section) => section.fields).reduce((state, field) => {
    state[field.name] = "";
    return state;
  }, {});

const toInputValue = (name, value) => {
  if (value === null || value === undefined) return "";
  if (DATE_FIELDS.includes(name)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().substring(0, 10);
  }
  if (NUMBER_FIELDS.includes(name)) return String(value);
  return String(value);
};

export const UpdatePolicyModal = ({
  showUser,
  toggleShowUser,
  refetch,
  selectedPolicy,
}) => {
  const [updatePolicy, setUpdatePolicy] = useState(emptyPolicy());
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!selectedPolicy) return;
    const next = emptyPolicy();
    Object.keys(next).forEach((name) => {
      next[name] = toInputValue(name, selectedPolicy[name]);
    });
    setUpdatePolicy(next);
  }, [selectedPolicy]);

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdatePolicy((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateCall = () => {
    setIsUpdating(true);
    const payload = { ...updatePolicy };
    NUMBER_FIELDS.forEach((name) => {
      payload[name] = updatePolicy[name] === "" ? undefined : Number(updatePolicy[name]);
    });

    updateSingleUploadedFile(selectedPolicy?.id, payload)
      .then((res) => {
        notify("success", "Success", res?.message || "Policy updated successfully");
        setIsUpdating(false);
        toggleShowUser();
        refetch();
      })
      .catch((error) => {
        notify("danger", "OOPS", error?.response?.data?.message || "Failed to update policy");
        setIsUpdating(false);
      });
  };

  return (
    <Modal
      show={showUser}
      onHide={toggleShowUser}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Update policy</Modal.Title>
          <div className="surface-card__meta">
            {selectedPolicy?.policyNumber
              ? `Policy ${selectedPolicy?.policyNumber}`
              : "Edit the policy details"}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="modal-body--scroll">
        <Form className="d-flex flex-column gap-4">
          {POLICY_SECTIONS.map((section) => (
            <div className="form-section" key={section.title}>
              <div className="form-section__title">{section.title}</div>
              <div className="form-grid">
                {section.fields.map((field) => (
                  <div key={field.name}>
                    <Form.Group>
                      <Form.Label>{field.label}</Form.Label>
                      <Form.Control
                        name={field.name}
                        type={field.type}
                        value={updatePolicy[field.name] || ""}
                        onChange={handleUpdateChange}
                      />
                    </Form.Group>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={toggleShowUser}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            handleUpdateCall();
          }}
          disabled={isUpdating}
        >
          {isUpdating ? "Updating..." : "Update policy"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
